import {
  createTheme,
  defaultVariantColorsResolver,
  type MantineTheme,
  type MantineThemeOverride,
  type VariantColorsResolver,
} from "@mantine/core";
import { autoContrastLabel } from "./contrast";
import { DARK_SCALE, GRAY_SCALE, RAMPS } from "./palette";

/**
 * Mantine's `autoContrast` threshold. Above it a filled surface takes the dark label, below it the light one.
 * 0.22 is deliberate: the vivid dark-mode anchors (brand #2F80FF at 0.232 luminance upward) must take the dark label,
 * because white on them falls under 4.5:1. `theme.test.ts` asserts the resulting label contrast for every ramp.
 */
export const LUMINANCE_THRESHOLD = 0.22;

/** The dark label `autoContrast` applies on light surfaces: the near-black page ground, not pure black. */
export const CONTRAST_DARK_LABEL = "#090a0c";

/**
 * The primary action (owner decision, 2026-09-17): a solid neutral fill, near-white on dark and near-black on light,
 * with the hover halo in `global.css`. Brand blue is the accent, never the primary fill.
 */
export const NEUTRAL_PRIMARY = {
  background: "light-dark(var(--mantine-color-gray-9), #fafafa)",
  hover: "light-dark(#2b2f36, #e0e0e0)",
  color: "light-dark(#ffffff, #171717)",
} as const;

/**
 * The tinted-surface recipe shared by badges, status badges, and outline buttons.
 *
 * Measured from the reference the owner supplied (2026-09-18): the accent itself carries the label, the fill is the
 * accent at a tenth of its strength, and a border at two fifths draws the shape. The earlier version had it backwards,
 * with a heavier fill, a washed-out label, and no border, which is why it read as flat. Percentages are alpha over
 * whatever surface the object sits on, so one badge looks the same on a panel, a table row, or a modal.
 */
const TINT = {
  fill: { light: 14, dark: 10 },
  hover: { light: 20, dark: 16 },
  border: { light: 78, dark: 40 },
  borderStrong: { light: 100, dark: 55 },
} as const;

/**
 * Light mode needs far more of the accent in its border than dark mode does. A 40% edge separates cleanly from a
 * near-black ground but vanishes against white, which is why the first light badges read as pale: measured, the light
 * border sat at 1.4:1 against its surface where dark's sat at 2.5:1. At 78% of the darker shade the two schemes
 * match, 2.0 to 3.1 against 1.8 to 2.5. The fill stays light in both, because the fill is not what gives a badge its
 * presence; pushing it instead would only crush the label's contrast.
 */
const TINT_SHADE = { fill: { light: 5, dark: 5 }, border: { light: 6, dark: 5 } } as const;

/** The label shade, one step lighter than the fill's accent in dark mode and much darker in light mode. */
const TINT_LABEL_SHADE = { light: 7, dark: 4 } as const;

type TintScheme = "light" | "dark";
type TintPart = "fill" | "border";

function tintedSurface(name: string) {
  const mix = (part: TintPart, scheme: TintScheme, percent: number): string =>
    `color-mix(in srgb, var(--mantine-color-${name}-${String(TINT_SHADE[part][scheme])}) ${String(percent)}%, transparent)`;
  const pair = (part: TintPart, steps: { readonly light: number; readonly dark: number }): string =>
    `light-dark(${mix(part, "light", steps.light)}, ${mix(part, "dark", steps.dark)})`;
  const label = (scheme: TintScheme): string =>
    `var(--mantine-color-${name}-${String(TINT_LABEL_SHADE[scheme])})`;
  return {
    background: pair("fill", TINT.fill),
    hover: pair("fill", TINT.hover),
    color: `light-dark(${label("light")}, ${label("dark")})`,
    border: `1px solid ${pair("border", TINT.border)}`,
    borderStrong: `1px solid ${pair("border", TINT.borderStrong)}`,
  };
}

/** The shade each scheme fills with, matching `primaryShade`. */
const FILLED_SHADE = { light: 7, dark: 5 } as const;

/** The readable label for a filled surface, resolved per scheme from the ramp instead of via `autoContrast`. */
function filledLabel(colors: Record<string, readonly string[]>, name: string): string {
  const ramp: readonly string[] = colors[name] ?? [];
  const light = ramp[FILLED_SHADE.light];
  const dark = ramp[FILLED_SHADE.dark];
  if (light === undefined || dark === undefined) return "var(--mantine-color-white)";
  const label = (fill: string): string =>
    autoContrastLabel(fill, LUMINANCE_THRESHOLD, "#ffffff", CONTRAST_DARK_LABEL);
  return `light-dark(${label(light)}, ${label(dark)})`;
}

/**
 * Variant recomposition (docs/UI_STANDARDS.md). Mantine injects its palette variables at runtime, which beats
 * stylesheet overrides, so variant colors are composed here.
 *
 * Four cases. The brand color's `filled` variant is the neutral primary button. Every other `filled` variant gets its
 * label computed here rather than by `autoContrast`, which never reaches the default resolver: it returned a
 * hardcoded white label, so every filled semantic button rendered white on a light fill in dark mode, down to 1.68:1
 * on the gold one. `light` is the tinted surface above, and `outline` is that surface without its fill.
 */
export const variantColorResolver: VariantColorsResolver = (input) => {
  const defaults = defaultVariantColorsResolver(input);
  const name = input.color ?? input.theme.primaryColor;
  const known = typeof name === "string" && input.theme.colors[name] !== undefined;
  if (!known) return defaults;

  if (name === input.theme.primaryColor && input.variant === "filled") {
    return { ...NEUTRAL_PRIMARY, border: "1px solid transparent" };
  }

  if (input.variant === "filled") {
    return { ...defaults, color: filledLabel(input.theme.colors, name) };
  }

  if (input.variant === "light") {
    return { ...defaults, ...tintedSurface(name) };
  }
  if (input.variant === "outline") {
    // The unfilled sibling of the badge: same label, a firmer border, and the tint arriving only on hover.
    const tint = tintedSurface(name);
    return {
      ...defaults,
      color: tint.color,
      border: tint.borderStrong,
      background: "transparent",
      hover: tint.background,
    };
  }
  return defaults;
};

export const themeOverride: MantineThemeOverride = {
  primaryColor: "brand",
  /** Light mode takes a darkened shade for contrast on white; dark mode takes the vivid anchor. */
  primaryShade: { light: 7, dark: 5 },
  autoContrast: true,
  luminanceThreshold: LUMINANCE_THRESHOLD,
  defaultRadius: "md",
  /** Standard panels 10px, hero panels 16px (docs/UI_STANDARDS.md, surface and elevation hierarchy). */
  radius: { xs: "4px", sm: "6px", md: "10px", lg: "12px", xl: "16px" },
  fontFamily:
    "'DM Sans Variable', 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  fontFamilyMonospace: "'JetBrains Mono', ui-monospace, SFMono-Regular, monospace",
  headings: {
    fontFamily: "'Manrope Variable', 'Manrope', 'DM Sans Variable', -apple-system, sans-serif",
    fontWeight: "700",
    sizes: {
      h1: { fontSize: "2.125rem", lineHeight: "1.1", fontWeight: "800" },
      h2: { fontSize: "1.375rem", lineHeight: "1.25" },
      h3: { fontSize: "1.125rem", lineHeight: "1.3" },
      h4: { fontSize: "1rem", lineHeight: "1.4" },
    },
  },
  colors: { ...RAMPS, dark: DARK_SCALE, gray: GRAY_SCALE },
  variantColorResolver,
  components: {
    /**
     * Standard panel: the `panel` surface, one quiet border, no shadow. Elevation comes from the surface ladder and
     * borders, not from drop shadows.
     */
    Card: {
      // Horizon's 24px corner (--how-card-radius in global.css), so every card matches the home page's.
      defaultProps: {
        radius: "var(--how-card-radius)",
        withBorder: true,
        padding: "lg",
        shadow: undefined,
      },
      classNames: { root: "how-panel" },
    },
    Paper: { defaultProps: { radius: "xl" } },
    /**
     * 36px desktop controls; `size="md"` remains available for large primary and mobile controls.
     *
     * The variant has to be resolved here rather than in CSS: Mantine 8 does not put `data-variant` on the Button
     * root (it expresses the variant through `--button-*` variables), so a stylesheet cannot tell a filled button
     * from an outlined one. `global.css` puts the hover halo on `.how-button--primary` only.
     */
    Button: {
      defaultProps: { size: "sm", radius: 999 },
      classNames: (_theme: MantineTheme, props: { readonly variant?: string | undefined }) => ({
        root:
          props.variant === undefined || props.variant === "filled"
            ? "how-button how-button--primary"
            : "how-button",
      }),
    },
    ActionIcon: { defaultProps: { size: "lg", variant: "subtle" } },
    TextInput: { defaultProps: { size: "sm", radius: "lg" } },
    PasswordInput: { defaultProps: { size: "sm", radius: "lg" } },
    Textarea: { defaultProps: { size: "sm", radius: "lg" } },
    Select: { defaultProps: { size: "sm", radius: "lg" } },
    MultiSelect: { defaultProps: { size: "sm", radius: "lg" } },
    NumberInput: { defaultProps: { size: "sm", radius: "lg" } },
    /** 40px standard rows: 9px of vertical spacing around a 21px line box plus the row separator. */
    Table: {
      defaultProps: { verticalSpacing: 14, horizontalSpacing: "lg" },
      classNames: { table: "how-table" },
    },
    // Status is scanned, not read: the badge gets real presence (26px tall, 13px label) rather than a 20px pill.
    Badge: { defaultProps: { radius: "xl", size: "lg" }, classNames: { root: "how-badge" } },
    NavLink: { classNames: { root: "how-navlink" } },
    Switch: { defaultProps: { color: "teal" } },
    Tooltip: {
      defaultProps: { withArrow: true, openDelay: 250 },
      classNames: { tooltip: "how-floating" },
    },
    Menu: { classNames: { dropdown: "how-floating" } },
    Popover: { classNames: { dropdown: "how-floating" } },
    HoverCard: { classNames: { dropdown: "how-floating" } },
    // The close button is where focus lands when a dialog opens, so it carries a name (#261).
    Modal: {
      defaultProps: {
        radius: "xl",
        overlayProps: { backgroundOpacity: 0.55, blur: 3 },
        closeButtonProps: { "aria-label": "Close" },
      },
      classNames: { content: "how-overlay", header: "how-overlay" },
    },
    Drawer: {
      defaultProps: {
        overlayProps: { backgroundOpacity: 0.55, blur: 3 },
        closeButtonProps: { "aria-label": "Close" },
      },
      classNames: { content: "how-overlay", header: "how-overlay" },
    },
    SegmentedControl: { defaultProps: { radius: 999 }, classNames: { root: "how-track" } },
    Tabs: { classNames: { list: "how-tabs-list" } },
  },
};

/** The single Mantine theme object for every HoW application surface. */
export const theme = createTheme(themeOverride);
